import React, { useCallback, useMemo } from "react";
import { formatWithMask, parseDigits, usePhoneMask } from "../../lib";

export type PhoneNumberInputProps = {
  id: string;
  mask?: string;
  onChange: (value: string) => void;
  value: string;
  error?: boolean;
  helperText?: string;
};

const DEFAULT_MASK = "______________";

const inputClasses =
  "w-full outline-0 text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base outline-none text-color-dark";

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  id,
  mask = DEFAULT_MASK,
  onChange,
  value: valueProp,
  error,
  helperText,
}) => {
  const { hasEmptyMask } = usePhoneMask({ mask });

  const maxDigits = useMemo(
    () => mask.split("").filter((c) => c === "_").length,
    [mask]
  );

  const value = useMemo(() => {
    const digits = parseDigits(valueProp);
    return formatWithMask(digits, mask);
  }, [valueProp, mask]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const digits = parseDigits(raw).slice(0, maxDigits);
      onChange(formatWithMask(digits, mask));
    },
    [onChange, mask, maxDigits]
  );

  return (
    <div className="flex flex-col gap-1 min-w-0 flex-1">
      <input
        id={id}
        type="tel"
        inputMode="numeric"
        placeholder={hasEmptyMask ? "Номер телефона" : mask}
        value={value}
        onChange={handleChange}
        className={inputClasses}
        style={{ fontVariantNumeric: "tabular-nums" }}
        aria-invalid={error}
        aria-describedby={helperText ? `${id}-helper` : undefined}
      />
      {helperText && (
        <span
          id={`${id}-helper`}
          className={`text-base ${error ? "text-color-danger flex items-center" : "text-color-dark/70"}`}
        >
          {helperText}
        </span>
      )}
    </div>
  );
};
